<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div class="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-md w-full">
      <!-- Modal Header -->
      <div class="bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-t-2xl text-white">
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-3">
            <i class="fas fa-lock text-2xl"></i>
            <h3 class="text-xl font-bold">Change PIN</h3>
          </div>
          <button 
            @click="close"
            class="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p class="text-purple-100 text-sm mt-2">Used for quick access and time tracking</p>
      </div>

      <!-- Modal Content -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
        <!-- Current PIN -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Current PIN
          </label>
          <input
            v-model="form.currentPin"
            type="password"
            inputmode="numeric"
            maxlength="4"
            pattern="[0-9]{4}"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white text-center text-2xl tracking-widest transition-colors"
            :class="{ 'border-red-500': errors.currentPin }"
            placeholder="••••"
            required
            @input="filterNumeric('currentPin')"
          />
          <p v-if="errors.currentPin" class="mt-1 text-sm text-red-500">
            {{ errors.currentPin }}
          </p>
        </div>

        <!-- New PIN -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            New PIN (4 digits)
          </label>
          <input
            v-model="form.newPin"
            type="password"
            inputmode="numeric"
            maxlength="4"
            pattern="[0-9]{4}"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white text-center text-2xl tracking-widest transition-colors"
            :class="{ 'border-red-500': errors.newPin }"
            placeholder="••••"
            required
            @input="filterNumeric('newPin')"
          />
          <p v-if="errors.newPin" class="mt-1 text-sm text-red-500">
            {{ errors.newPin }}
          </p>
        </div>

        <!-- Confirm New PIN -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Confirm New PIN
          </label>
          <input
            v-model="form.confirmPin"
            type="password"
            inputmode="numeric"
            maxlength="4"
            pattern="[0-9]{4}"
            class="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:text-white text-center text-2xl tracking-widest transition-colors"
            :class="{ 'border-red-500': errors.confirmPin }"
            placeholder="••••"
            required
            @input="filterNumeric('confirmPin')"
          />
          <p v-if="errors.confirmPin" class="mt-1 text-sm text-red-500">
            {{ errors.confirmPin }}
          </p>
        </div>

        <!-- Security Note -->
        <div class="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p class="text-xs text-yellow-800 dark:text-yellow-400">
            <i class="fas fa-info-circle mr-1"></i>
            <strong>Note:</strong> Your PIN is used for quick access and time tracking only. For security, always use your password for full account access.
          </p>
        </div>

        <!-- Error Message -->
        <div v-if="errors.general" class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p class="text-sm text-red-600 dark:text-red-400">
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ errors.general }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <button
            type="button"
            @click="close"
            class="flex-1 px-4 py-3 bg-gray-200 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors font-medium"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            :disabled="loading || !isFormValid"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-check"></i>
            {{ loading ? 'Updating...' : 'Update PIN' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/composables/useToast';
import { api } from '@/services/api';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['close', 'success']);

const authStore = useAuthStore();
const toast = useToast();

const loading = ref(false);

const form = ref({
  currentPin: '',
  newPin: '',
  confirmPin: ''
});

const errors = ref({
  currentPin: '',
  newPin: '',
  confirmPin: '',
  general: ''
});

const isFormValid = computed(() => {
  return form.value.currentPin.length === 4 && 
         form.value.newPin.length === 4 && 
         form.value.confirmPin.length === 4 &&
         form.value.newPin === form.value.confirmPin;
});

const filterNumeric = (field) => {
  form.value[field] = form.value[field].replace(/[^0-9]/g, '');
  errors.value[field] = '';
  errors.value.general = '';
};

const handleSubmit = async () => {
  // Clear previous errors
  errors.value = {
    currentPin: '',
    newPin: '',
    confirmPin: '',
    general: ''
  };

  // Validate form
  if (form.value.currentPin.length !== 4) {
    errors.value.currentPin = 'PIN must be 4 digits';
    return;
  }

  if (form.value.newPin.length !== 4) {
    errors.value.newPin = 'PIN must be 4 digits';
    return;
  }

  if (form.value.newPin !== form.value.confirmPin) {
    errors.value.confirmPin = 'PINs do not match';
    return;
  }

  if (form.value.currentPin === form.value.newPin) {
    errors.value.newPin = 'New PIN must be different from current PIN';
    return;
  }

  loading.value = true;

  try {
    // Call backend API to update PIN
    await api.put(`/users/${authStore.user.id}/pin`, {
      currentPin: form.value.currentPin,
      newPin: form.value.newPin
    });

    toast.success('PIN updated successfully!');
    emit('success');
    close();
  } catch (error) {
    console.error('PIN change error:', error);
    
    if (error.response?.status === 401) {
      errors.value.currentPin = 'Current PIN is incorrect';
    } else {
      errors.value.general = error.response?.data?.error || 'Failed to update PIN. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};

const close = () => {
  if (!loading.value) {
    // Reset form
    form.value = {
      currentPin: '',
      newPin: '',
      confirmPin: ''
    };
    errors.value = {
      currentPin: '',
      newPin: '',
      confirmPin: '',
      general: ''
    };
    emit('close');
  }
};
</script>

